import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Icons from '@shared/components/core/images/Icons';
import { ButtonSize, ICoreButtonProps } from '@shared/models/components/ICoreButtonProps';
import { forwardRef } from 'react';
import { NavLink } from 'react-router';
import { buttonBaseStyles, disabledButtonStyles, sizeStyles } from './CommonButtonStyles';

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ICoreButtonProps>(
    (
        {
            type = 'button',
            $color = 'primary',
            $size = 'medium',
            disabled = false,
            onClick,
            href,
            to,
            target,
            iconSize = 24,
            startIcon,
            endIcon,
            startComponent,
            endComponent,
            children,
            ...restProps
        }: ICoreButtonProps,
        ref
    ) => {
        const commonProps = {
            $color,
            disabled,
            target,
            $size,
        };
        endIcon = target === '_blank' && href?.startsWith('http') ? 'externalLink' : endIcon;
        if (href && !target) target = '_self';
        const ButtonComponent = {
            primary: PrimaryButton,
            secondary: SecondaryButton,
            accent: AccentButton,
            simple: SimpleButton,
            quick: QuickButton,
        };
        const LinkComponent = {
            primary: PrimaryLink,
            secondary: SecondaryLink,
            accent: AccentLink,
            simple: SimpleLink,
            quick: QuickLink,
        };
        const NavLinkComponent = {
            primary: PrimaryNavLink,
            secondary: SecondaryNavLink,
            accent: AccentNavLink,
            simple: SimpleNavLink,
            quick: QuickNavLink,
        };

        if (href) {
            const StyledLink = LinkComponent[$color];
            return (
                <StyledLink
                    as="a"
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className="core-button"
                    href={href}
                    {...commonProps}
                    {...restProps}>
                    {startComponent}
                    {startIcon && <Icons name={startIcon} size={iconSize} />}
                    {children}
                    {endComponent}
                    {endIcon && <Icons name={endIcon} size={iconSize} />}
                </StyledLink>
            );
        } else if (to) {
            const { $color: _color, ...safeProps } = { ...commonProps, ...restProps };
            const StyledNavLink = NavLinkComponent[_color];
            return (
                <StyledNavLink
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className="core-button"
                    viewTransition
                    to={to}
                    {...commonProps}
                    {...safeProps}>
                    {startIcon && <Icons name={startIcon} size={iconSize} />}
                    {startComponent}
                    {children}
                    {endIcon && <Icons name={endIcon} size={iconSize} />}
                    {endComponent}
                </StyledNavLink>
            );
        } else {
            const StyledButton = ButtonComponent[$color];
            return (
                <StyledButton
                    ref={ref as React.Ref<HTMLButtonElement>}
                    className="core-button"
                    type={type}
                    onClick={onClick}
                    {...commonProps}
                    {...restProps}>
                    {startComponent}
                    {startIcon && <Icons name={startIcon} size={iconSize} />}
                    {children}
                    {endComponent}
                    {endIcon && <Icons name={endIcon} size={iconSize} />}
                </StyledButton>
            );
        }
    }
);

const PrimaryButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$size' && prop !== '$color',
})<{ $size: ButtonSize }>(
    ({ theme, $size, disabled }) => css`
        ${buttonBaseStyles}
        font-weight: 700;
        background: ${theme.button.primary.background};
        ${theme.button.primary.backgroundImage &&
        `background-image: linear-gradient(54deg, #062844 4.52%, #4b2b8e 59.43%, #ee486f 125.41%)`};
        text-decoration: none;
        border: 1px solid ${theme.button.primary.border};
        color: ${theme.button.primary.text};
        &:hover {
            svg {
                color: ${theme.button.primary.icon};
            }
            color: ${theme.button.primary.hoverText};
            background: ${theme.button.primary.hoverBackground};
        }
        ${$size && sizeStyles[$size]}
        ${disabled && disabledButtonStyles}
    `
);

const SecondaryButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$size' && prop !== '$color',
})<{ $size: ButtonSize }>(
    ({ theme, $size, disabled }) => css`
        ${buttonBaseStyles}
        font-weight: 700;
        text-decoration: none;
        background: ${theme.button.secondary.background};
        border: solid 1px ${theme.button.secondary.border};
        color: ${theme.button.secondary.text};
        &:hover {
            svg {
                color: ${theme.button.secondary.icon};
            }
            color: ${theme.button.secondary.hoverText};
            background: ${theme.button.secondary.hoverBackground};
        }
        ${$size && sizeStyles[$size]}
        ${disabled && disabledButtonStyles}
    `
);

const AccentButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$size' && prop !== '$color',
})<{ $size: ButtonSize }>(
    ({ theme, $size, disabled }) => css`
        ${buttonBaseStyles}
        background: ${theme.button.accent.background};
        color: ${theme.button.accent.text};
        text-decoration: none;
        border-radius: 2rem;
        border: transparent 1px solid;
        font-weight: 300;
        &:hover {
            border: ${theme.button.accent.border} 1px solid;
            background: ${theme.button.accent.hoverBackground};
            color: ${theme.button.accent.hoverText};
            svg {
                color: ${theme.button.accent.icon};
            }
        }

        ${$size && sizeStyles['quick']}
        ${disabled && disabledButtonStyles}
    `
);

const SimpleButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$size' && prop !== '$color',
})<{ $size: ButtonSize }>(
    ({ theme, $size, disabled }) => css`
        ${buttonBaseStyles}
        background: ${theme.button.simple.background};
        color: ${theme.button.simple.text};
        text-decoration: underline !important;
        text-decoration-color: ${theme.button.simple.text};
        border: none;
        &:hover {
            text-decoration-color: ${theme.button.simple.hoverText} !important;
            filter: none;
            box-shadow: 0px 0px 0px 1px ${theme.button.simple.text};
        }
        ${sizeStyles[$size]}
        ${disabled && disabledButtonStyles}
        font-size: 1rem;
        min-width: fit-content;
    `
);

const QuickButton = styled('button', {
    shouldForwardProp: (prop) => prop !== '$size' && prop !== '$color',
})<{ $size: ButtonSize }>(
    ({ theme, $size, disabled }) => css`
        ${buttonBaseStyles}
        padding: 0.625rem 0.875rem;
        border-radius: 1.875rem;
        background: ${theme.button.quick.background};
        color: ${theme.button.quick.text};
        text-decoration: none;
        border: ${theme.button.quick.border} 1px solid;
        font-weight: 400;
        font-family: 'Archivo', sans-serif;
        font-size: 1rem;
        svg {
            color: ${theme.button.quick.border};
        }
        &:hover {
            border: ${theme.button.quick.border} 1px solid;
            background: ${theme.button.quick.hoverBackground};
            color: ${theme.button.quick.hoverText};
            text-decoration: underline !important;
            text-decoration-color: ${theme.button.quick.border};
            svg {
                color: ${theme.button.quick.icon};
            }
        }
        ${$size && sizeStyles[$size]}
        ${disabled && disabledButtonStyles}
    `
);

const PrimaryLink = styled(PrimaryButton.withComponent('a'))``;
const SecondaryLink = styled(SecondaryButton.withComponent('a'))``;
const AccentLink = styled(AccentButton.withComponent('a'))``;
const SimpleLink = styled(SimpleButton.withComponent('a'))``;
const QuickLink = styled(QuickButton.withComponent('a'))``;

const PrimaryNavLink = styled(PrimaryButton.withComponent(NavLink))``;
const SecondaryNavLink = styled(SecondaryButton.withComponent(NavLink))``;
const AccentNavLink = styled(AccentButton.withComponent(NavLink))``;
const SimpleNavLink = styled(SimpleButton.withComponent(NavLink))``;
const QuickNavLink = styled(QuickButton.withComponent(NavLink))``;

export default Button;
